/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import * as Yup from 'yup';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useReactiveVar } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useForm, yupResolver } from '@mantine/form';
import { showNotification } from '@mantine/notifications';

// UI lib components
import {
  Stack,
  Container,
  Text,
  Button,
  MultiSelect,
  Box,
  Group,
} from '@mantine/core';

// UI local components
import ConfirmBar from '@ui-kit/molecules/ConfirmBar';
import QuestionPrioritySelect from '@app/shared/ui-kit/molecules/QuestionPrioritySelect';
import SupportTextarea from '@app/features/Support/SupportTextarea';
import ResourceSelector from '@features/AIAssistant/PageAIAssistant/SectionRename/ResourceSelector';
import {
  useQaFileUploadList,
  QaFileUploadButton,
  QaFileList,
  QaFileUploadProvider,
} from '@features/Support/QaFileUploadList';

// Assets
import { IconPaperclip as IconSelectResource } from '@tabler/icons-react';

// Store
import useWorkspace from '@features/Workspace/store/hooks/useWorkspace';
import dialogStoreVar from '@features/Dialog/store/dialogStore';

// Hooks
import useRefetchQuestions from '@features/Support/hooks/useRefetchQuestions';

// Local API & Types
import {
  ListQuestionsQueryVariables,
  QuestionPriority,
  ResourceIndexView,
  useCreateQuestionMutation,
  useTopicsQuery,
} from '@app-types/blackroom-api';
import { DialogQuestionProps, DialogQuestionFormSchema } from './types';
import LIST_QUESTIONS from '@app/features/Support/api/listQuestions';

// Style
import * as classes from './styles/index.css';
import { ICON_SIZE_MAP } from '@app/shared/ui-kit/icons-sizes';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

function DialogQuestion({ resourceId, ...props }: DialogQuestionProps) {
  /* ********************************** HOOKS ********************************* */

  // Ref
  const focusRef = useRef<HTMLInputElement>(null);

  // State
  const [openResourceTable, setOpenResourceTable] = useState<boolean>(false);
  const [selectedResources, setSelectedResources] = useState<
    ResourceIndexView[]
  >([]);

  // External file upload
  const {
    externalFiles,
    pendingFiles,
    isUploading,
    handleFileUpload,
    removeFile,
    cancelUpload,
    hasFiles,
  } = useQaFileUploadList();

  // Store
  const dialogStore = useReactiveVar(dialogStoreVar);
  const { dialogIdentifier, closeDialog } = dialogStore ?? {};
  const workspace = useWorkspace();

  // API
  const [createQuestion] = useCreateQuestionMutation();
  const { data: topicsData } = useTopicsQuery({
    variables: { workspaceId: workspace.id },
  });

  // Locale
  const { t } = useTranslation();

  const topicOptions = useMemo(
    () =>
      (topicsData?.topics ?? []).map((topic) => ({
        value: topic.id,
        label: topic.name,
      })),
    [topicsData],
  );

  const refetchQuestions = useRefetchQuestions();

  // Form
  const formSchema = Yup.object().shape({
    title: Yup.string()
      .trim()
      .required(t('Common:validation.required') as string),
    priority: Yup.mixed<QuestionPriority>()
      .oneOf(Object.values(QuestionPriority))
      .required(),
    description: Yup.string().trim(),
    topicIds: Yup.array().of(Yup.string()),
  });
  const form = useForm<DialogQuestionFormSchema>({
    initialValues: {
      title: '',
      priority: QuestionPriority.Low,
      description: '',
    },
    validate: yupResolver(formSchema),
    onValuesChange: (formData) => {
      form.validate();
    },
  });

  // Side-effects
  useEffect(
    function onMount() {
      if (dialogIdentifier === 'dialog-support-question') {
        focusRef.current?.focus();
      }
    },
    [dialogIdentifier],
  );

  /* ******************************** CALLBACKS ******************************* */

  async function onSubmit({ ...formArgs }: DialogQuestionFormSchema) {
    try {
      let refetchQueries;
      // Case: dialog triggered from page file
      if (resourceId) {
        refetchQueries = [
          {
            query: LIST_QUESTIONS,
            variables: {
              filter: {
                workspaceId: workspace.id,
                resourceId,
              },
              limit: 1_000,
            } as ListQuestionsQueryVariables,
          },
        ];
      }

      closeDialog?.('dialog-support-question');

      const newResources = externalFiles.length > 0 ? externalFiles : undefined;

      await createQuestion({
        variables: {
          createQuestionInput: {
            workspaceId: workspace.id,
            questionsInput: [
              {
                ...formArgs,
                resourceIds: resourceId
                  ? [resourceId]
                  : selectedResources.map((r) => r.id),
                newResources,
              },
            ],
          },
        },
        refetchQueries,
      });

      // Case: dialog triggered from page support
      if (!resourceId) {
        refetchQuestions();
      }
    } catch (error) {
      showNotification({
        title: t('Common:errors.genericTitle'),
        message: t('Support:Common.errors.readonlyAccess'),
        color: 'blackroomBlack',
      });
      console.error(error);
    }
  }

  /* ******************************** RENDERING ******************************* */

  return (
    <Container w="780px" fluid>
      <Stack align="stretch" justify="space-between" gap="lg" h="100%">
        <form onSubmit={(e) => e.preventDefault()}>
          <Stack>
            <QuestionPrioritySelect
              label={t(
                'Support:DialogQuestionCreate.form.fields.priority.label',
              )}
              withAsterisk
              allowDeselect={false}
              selectedPriority={form.values.priority}
              {...form.getInputProps('priority')}
            />
            <SupportTextarea
              label={t('Support:DialogQuestionCreate.form.fields.title.label')}
              placeholder={t(
                'Support:DialogQuestionCreate.form.fields.title.placeholder',
              )}
              classNames={{
                input: classes.questionTextareaInput,
              }}
              autoFocus
              withAsterisk
              {...form.getInputProps('title')}
            />
            <SupportTextarea
              label={t(
                'Support:DialogQuestionCreate.form.fields.description.label',
              )}
              placeholder={t(
                'Support:DialogQuestionCreate.form.fields.description.placeholder',
              )}
              {...form.getInputProps('description')}
            />
            <MultiSelect
              size="md"
              label={t('Support:DialogQuestionCreate.form.fields.topics.label')}
              placeholder={t(
                'Support:DialogQuestionCreate.form.fields.topics.placeholder',
              )}
              data={topicOptions}
              {...form.getInputProps('topicIds')}
            />
            {!resourceId && (
              <Stack align="flex-start" gap={0}>
                <Group gap="md">
                  <Button
                    variant="transparent"
                    leftSection={
                      <IconSelectResource size={ICON_SIZE_MAP.small} />
                    }
                    classNames={{
                      section: classes.selectResourceButtonSection,
                    }}
                    p={0}
                    onClick={() => setOpenResourceTable(!openResourceTable)}
                  >
                    <Text c="blackroomBlack" fz="sm">
                      {t(
                        'Support:QuestionMenu.replyInThreadForm.fields.resource.label',
                      )}
                    </Text>
                  </Button>
                  <QaFileUploadButton
                    isUploading={isUploading}
                    onFilesSelected={handleFileUpload}
                    buttonSectionClassName={classes.selectResourceButtonSection}
                  />
                </Group>
                {openResourceTable && (
                  <Box h={400} w="100%">
                    <ResourceSelector
                      selectedResources={selectedResources}
                      setSelectedResources={setSelectedResources}
                    />
                  </Box>
                )}
                <QaFileUploadProvider
                  externalFiles={externalFiles}
                  pendingFiles={pendingFiles}
                  removeFile={removeFile}
                  cancelUpload={cancelUpload}
                >
                  {(selectedResources.length > 0 || hasFiles) && (
                    <Box mt={8}>
                      <QaFileList
                        resources={selectedResources}
                        onRemoveResource={(resourceId) =>
                          setSelectedResources(
                            selectedResources.filter(
                              (r) => r.id !== resourceId,
                            ),
                          )
                        }
                      />
                    </Box>
                  )}
                </QaFileUploadProvider>
              </Stack>
            )}
          </Stack>
        </form>
        <ConfirmBar
          labelProps={{
            confirm: t('Common:cta.add'),
            cancel: t('Common:cta.cancel'),
          }}
          onConfirm={form.onSubmit(function cb(formValues) {
            onSubmit(formValues);
          })}
          onCancel={() => {
            dialogStore?.closeDialog?.('dialog-support-question');
          }}
          disabled={!form.isValid() || isUploading}
        />
      </Stack>
    </Container>
  );
}

/* **************************** DIALOG INVOKATION *************************** */

export default DialogQuestion;
